import type { SceneTrainingSnapshot } from "./types";

const DB_VERSION = 1;
const STORE = "scene-sessions";

export interface SceneEvent {
  readonly schemaVersion: 1;
  readonly eventId: string;
  readonly eventType: string;
  readonly utcTimestamp: string;
  readonly monotonicTimeMs: number;
  readonly participantId: string;
  readonly sessionId: string;
  readonly mode: "formal" | "dev_fast";
  readonly sessionSeed: number;
  readonly sceneId: string | null;
  readonly decisionIndex: number | null;
  readonly payload: Readonly<Record<string, unknown>>;
}

export interface SceneSessionRecord {
  readonly schemaVersion: 1;
  readonly participantId: string;
  readonly sessionId: string;
  readonly catalogVersion: string;
  readonly status: "active" | "complete" | "incomplete";
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly engine: SceneTrainingSnapshot;
  readonly events: readonly SceneEvent[];
  readonly activeElapsedMs: number;
  /** Effective-session clock value captured when the current scene began. */
  readonly sceneStartedElapsedMs?: number;
  readonly activeClockStartedAt: number | null;
}

export class SceneSessionStore {
  private database: IDBDatabase | null = null;

  constructor(private readonly factory: IDBFactory = indexedDB) {}

  async open(): Promise<void> {
    if (this.database) return;
    this.database = await new Promise((resolve, reject) => {
      const request = this.factory.open("auditory-scene-training", DB_VERSION);
      request.onupgradeneeded = () => {
        if (!request.result.objectStoreNames.contains(STORE)) {
          const store = request.result.createObjectStore(STORE, { keyPath: "sessionId" });
          store.createIndex("updatedAt", "updatedAt");
          store.createIndex("status", "status");
        }
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error ?? new Error("Scene session database failed to open"));
    });
  }

  async save(record: SceneSessionRecord): Promise<void> {
    const transaction = this.requireDatabase().transaction(STORE, "readwrite");
    transaction.objectStore(STORE).put(record);
    await transactionDone(transaction);
  }

  async get(sessionId: string): Promise<SceneSessionRecord | null> {
    const transaction = this.requireDatabase().transaction(STORE, "readonly");
    const result = await requestResult<SceneSessionRecord | undefined>(transaction.objectStore(STORE).get(sessionId));
    await transactionDone(transaction);
    return result ?? null;
  }

  async list(): Promise<SceneSessionRecord[]> {
    const transaction = this.requireDatabase().transaction(STORE, "readonly");
    const result = await requestResult<SceneSessionRecord[]>(transaction.objectStore(STORE).getAll());
    await transactionDone(transaction);
    return result.sort((left, right) => right.updatedAt.localeCompare(left.updatedAt));
  }

  async remove(sessionId: string): Promise<void> {
    const transaction = this.requireDatabase().transaction(STORE, "readwrite");
    transaction.objectStore(STORE).delete(sessionId);
    await transactionDone(transaction);
  }

  private requireDatabase(): IDBDatabase {
    if (!this.database) throw new Error("Scene session store is not open");
    return this.database;
  }
}

export function createSceneEvent(
  record: Pick<SceneSessionRecord, "participantId" | "sessionId" | "engine">,
  eventType: string,
  sceneId: string | null,
  decisionIndex: number | null,
  payload: Record<string, unknown> = {},
): SceneEvent {
  return {
    schemaVersion: 1,
    eventId: crypto.randomUUID(),
    eventType,
    utcTimestamp: new Date().toISOString(),
    monotonicTimeMs: performance.now(),
    participantId: record.participantId,
    sessionId: record.sessionId,
    mode: record.engine.mode,
    sessionSeed: record.engine.seed,
    sceneId,
    decisionIndex,
    payload,
  };
}

export async function exportSceneEvents(record: SceneSessionRecord): Promise<{ filename: string; checksum: string }> {
  const text = record.events.map((event) => JSON.stringify(event)).join("\n") + (record.events.length ? "\n" : "");
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(text));
  const checksum = [...new Uint8Array(digest)].map((value) => value.toString(16).padStart(2, "0")).join("");
  const safe = (value: string) => value.replaceAll(/[^a-zA-Z0-9_-]/g, "-").slice(0, 48) || "unknown";
  const filename = `scene_training_${safe(record.participantId)}_${safe(record.sessionId)}.jsonl`;
  download(filename, text, "application/x-ndjson");
  download(`${filename}.sha256`, `${checksum}  ${filename}\n`, "text/plain");
  return { filename, checksum };
}

function download(filename: string, body: string, type: string): void {
  const url = URL.createObjectURL(new Blob([body], { type }));
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  window.setTimeout(() => URL.revokeObjectURL(url), 0);
}

function requestResult<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error("IndexedDB request failed"));
  });
}

function transactionDone(transaction: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error ?? new Error("IndexedDB transaction failed"));
    transaction.onabort = () => reject(transaction.error ?? new Error("IndexedDB transaction aborted"));
  });
}
